import { http } from 'cdui-js';

// 券商收款银行 /2go/broker/banks
export const fetchBrokerBanks = async (currency) => {
  const res = await http.get(`/2go/broker/banks?currency=${currency}`);

  return res;
};

// 自己的银行：/2go/user/banks。不要传 subUserID
export const fetchUserBanks = async (currency) => {
  const res = await http.get(`/2go/user/banks?currency=${currency}`);
  return res;
};

export type QuoteCurrency = 'HKD' | 'USD' | 'CNY';

/** productType：S 证券 / C 加密。type：C 现金 / M 融资 */
export type StockAccountMeta = {
  accountNum?: string;
  subUserID?: string;
  subUserId?: string;
  productType?: string;
  type?: string;
};

/** 单币种可提现 */
export type CurrencyWithdrawal = {
  currency?: QuoteCurrency | string;
  withdrawalBal?: number | string;
};

/** 账户可提现（实时，金额不缓存） */
export type StockAccountWithdrawalBal = StockAccountMeta & {
  currencies?: CurrencyWithdrawal[];
};

export type StockAccountsMetaQuery = {
  /** true=实时调 2go 并刷新缓存；默认 false 读缓存 */
  realtime?: boolean;
  /** S=股票，C=加密；不传不过滤 */
  productType?: 'S' | 'C' | string;
  market?: string;
  currency?: QuoteCurrency | string;
};

export type StockAccountsWithdrawalBalQuery = {
  accountNum?: string;
  currency?: QuoteCurrency | string;
};

const toQuery = (params?: Record<string, string | boolean | undefined | null>) => {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value == null || value === '') return;
    qs.set(key, String(value));
  });
  const text = qs.toString();
  return text ? `?${text}` : '';
};

/**
 * 账户列表元数据（无金额）。入金 / 银行 / 资金记录账户下拉用这一支。
 * 默认读缓存；需要刷新传 realtime=true。
 */
export const fetchStockAccountsMeta = async (query?: StockAccountsMetaQuery, init?: RequestInit) => {
  return http.get(
    `/2go/user/stockAccountsMeta${toQuery({
      realtime: query?.realtime,
      productType: query?.productType,
      market: query?.market,
      currency: query?.currency,
    })}`,
    init,
  ) as Promise<StockAccountMeta[]>;
};

/**
 * 账户可提现金额（实时 getUserAccounts withAsset=true）。
 * 出金 / 货币兑换展示与校验余额用这一支。可按 accountNum、currency 过滤。
 */
export const fetchStockAccountsWithdrawalBal = async (
  query?: StockAccountsWithdrawalBalQuery,
  init?: RequestInit,
) => {
  return http.get(
    `/2go/user/stockAccountsWithdrawalBal${toQuery({
      accountNum: query?.accountNum,
      currency: query?.currency,
    })}`,
    init,
  ) as Promise<StockAccountWithdrawalBal[]>;
};

/** 把可提现 currencies 合并进 meta 列表，便于复用原有账户解析 */
export const mergeStockAccountWithdrawalBal = (
  metas: StockAccountMeta[],
  bals: StockAccountWithdrawalBal[],
) => {
  const map = new Map(
    (bals || []).map((row) => [String(row?.accountNum ?? '').trim(), row] as const),
  );
  return (metas || []).map((meta) => {
    const num = String(meta?.accountNum ?? '').trim();
    const bal = num ? map.get(num) : undefined;
    if (!bal) return meta;
    return {
      ...meta,
      productType: bal.productType ?? meta.productType,
      type: bal.type ?? meta.type,
      currencies: bal.currencies,
    };
  });
};

/** @deprecated 改用 fetchStockAccountsMeta + fetchStockAccountsWithdrawalBal */
export type StockAccountBalance = CurrencyWithdrawal & {
  availCashBal?: number | string;
  lockupCashBal?: number | string;
  netAsset?: number | string;
  ledgerCashBal?: number | string;
  profitAndLoss?: number | string;
  stockMarketValue?: { amount?: number | string; currency?: string };
};

/** @deprecated 改用 StockAccountMeta */
export type StockAccountOverview = StockAccountMeta & {
  stockAccounts?: StockAccountBalance[];
  currencies?: CurrencyWithdrawal[];
};

/** @deprecated 改用 fetchStockAccountsMeta / fetchStockAccountsWithdrawalBal */
export const fetchStockAccountsOverview = async (accountNum?: string) => {
  const query = accountNum ? `?accountNum=${encodeURIComponent(accountNum)}` : '';
  return http.get(`/2go/user/stockAccountsOverview${query}`) as Promise<StockAccountOverview[]>;
};

// 快捷入金
export const ddiDeposits = async (data) => {
  const res = await http.post(`/2go/fund/deposits/ddi`, data);
  return res;
};

export const deposits = async (data) => {
  const res = await http.post(`/2go/fund/deposits`, data);
  return res;
};

/** 真实请求 path。OPS 签名 path 是 /2go/funds/withdrawals，这里保持 fund 无 s */
const WITHDRAWALS_PATH = '/2go/fund/withdrawals';

const withdrawalsRequestPath = (urlPath?: string) => {
  const path = String(urlPath || '')
    .trim()
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/^\/api\/v1(?=\/)/, '')
    .split('?')[0]
    .replace(/\/+$/, '');
  if (path === WITHDRAWALS_PATH) return path;
  return WITHDRAWALS_PATH;
};

// 出金 提款。body / 签名头用 App 回包，不要在这里改 password、nonce、timestamp
export const withDrawalsFund = async (
  data: Record<string, unknown>,
  options?: { headers?: Record<string, string>; path?: string },
) => {
  const headers: Record<string, string> = {
    'X-No-Toast': '1',
    ...(options?.headers || {}),
  };
  const res = await http.post(withdrawalsRequestPath(options?.path), data, { headers });
  return res;
};

// 前置条件 出入金前置数据：/2go/fund/depositWithdrawPrepare
// 人民币：CNY，港元：HKD，美元：USD
export const fetchDeWiInfo = async ({
  type,
  currency,
  method,
  accountNum,
}: {
  type: string;
  currency: string;
  method: string;
  accountNum?: string;
}) => {
  const params = new URLSearchParams({ currency, type, method });
  if (accountNum != null) params.set('accountNum', accountNum);
  return http.get(`/2go/fund/depositWithdrawPrepare?${params.toString()}`);
};

// 出金申请列表
export const fetchWithdrawalsList = async () => {
  const res = await http.get(`/2go/fund/withdrawals`);
  return res;
};

// 入金申请列表
export const fetchDepositsList = async () => {
  const res = await http.get(`/2go/fund/deposits`);
  return res;
};

// 出金验证码 sendCodeType account useType
interface VerifySendCodeTypes {
  sendCodeType: string;
  account: string;
  useType: string;
}

export const verifySendCode = async (data: VerifySendCodeTypes) => {
  const res = await http.post(`/user/code/verifySend/account`, data);
  return res;
};
